import { queryKeys } from "@/lib/queryKeys";
import {
    useAssistantHistory,
    useConversation,
    type ConversationSummary,
} from "@cs_teamwaydev/mailvista-sdk_react";
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";

/** History (step-based contract): the list of the past conversations kept by the app. */
export const useConversationsList = () => {
    const queryClient = useQueryClient();
    const conversation = useConversation();

    const refresh = () =>
        queryClient.invalidateQueries({
            queryKey: queryKeys.assistant.conversations(),
        });

    // The conversation shows at the top of the list as soon as its question is sent, titled with its
    // first question, before the turn is kept.
    const showInHistory = (question: string) => {
        const id = conversation.getConversationId();
        if (!conversation.hasConversationMemory() || !id) return;
        const now = new Date().toISOString();
        queryClient.setQueryData<ConversationSummary[]>(
            queryKeys.assistant.conversations(),
            (list) => {
                if (!list) return list;
                const known = list.find((item) => item.id === id);
                const first = question.replace(/\s+/g, " ").trim();
                const line: ConversationSummary = known
                    ? { ...known, updatedAt: now }
                    : {
                          id,
                          title:
                              first.length > 60
                                  ? `${first.slice(0, 60).trimEnd()}…`
                                  : first,
                          createdAt: now,
                          updatedAt: now,
                          turnCount: 0,
                      };

                return [line, ...list.filter((item) => item.id !== id)];
            },
        );
    };

    return { refresh, showInHistory };
};

interface ConversationHistoryOptions {
    /** A keyword to search the conversations for (empty: no search). */
    keyword: string;
    /** The conversation shown in the panel. */
    currentId?: string;
    /** The conversation shown was deleted. */
    onCurrentDeleted: () => void;
}

type ConversationHistory = {
    conversations: UseQueryResult<ConversationSummary[]>;
    results: UseQueryResult<ConversationSummary[]>;
    rename: UseMutationResult<void, Error, { id: string; title: string }>;
    remove: UseMutationResult<void, Error, string>;
    removeAll: UseMutationResult<void, Error, void>;
};

/** The past conversations, a keyword search among them, and what can be done to them. */
const useConversationHistory = ({
    keyword,
    currentId,
    onCurrentDeleted,
}: ConversationHistoryOptions): ConversationHistory => {
    const history = useAssistantHistory();
    const { refresh } = useConversationsList();

    const conversations = useQuery<ConversationSummary[]>({
        queryKey: queryKeys.assistant.conversations(),
        queryFn: () => history.listConversations(),
        // Opened: the latest list. A saved turn or an action marks it stale (invalidation), so it is
        // read again; otherwise a list read less than 30 s ago is shown as is, without a new request
        // at each opening of the panel (audit de performance du 15/10, P4-09).
        staleTime: 30_000,
    });
    const results = useQuery<ConversationSummary[]>({
        queryKey: queryKeys.assistant.conversationSearch(keyword),
        queryFn: () => history.listConversations(keyword),
        enabled: keyword.length > 0,
    });
    const rename = useMutation({
        mutationFn: ({ id, title }: { id: string; title: string }) =>
            history.renameConversation(id, title),
        onSettled: refresh,
    });
    const remove = useMutation({
        mutationFn: (id: string) => history.deleteConversation(id),
        onSuccess: (_result, id) => {
            if (id === currentId) onCurrentDeleted();
        },
        onSettled: refresh,
    });
    const removeAll = useMutation({
        mutationFn: () => history.deleteAllConversations(),
        onSuccess: onCurrentDeleted,
        onSettled: refresh,
    });

    return { conversations, results, rename, remove, removeAll };
};

export default useConversationHistory;
